import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Loader2, Mic, Save, Upload } from "lucide-react";
import { apiClient } from "@/lib/auth";
import { rerecordAllBeats } from "@/components/clientDemo/tour/RerecordAllButton";

const DEFAULT_TITLES = [
  "Lead Intelligence & Acquisition", "Onboarding & Merchant Verification", "Portfolio Risk & Dispute Operations",
  "Financial Accounting & Terminal Logistics", "Automation, Analytics & AI Support", "Custom Specialty Engines",
];
const inputCls = "rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-white text-sm";

export const isTemplated = (d) => d.slug === "veroogle" || ["veroogle", "exchange"].includes(d.template);

export default function DemoTemplateSettings({ demo, onSaved }) {
  const [open, setOpen] = useState(false);
  const [accent, setAccent] = useState(demo.accent_color || "#06b6d4");
  const [titles, setTitles] = useState(() => DEFAULT_TITLES.map((_, i) => demo.phase_titles?.[i] || ""));
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState(null);
  const s = demo.slug;

  const loadStatus = () => apiClient.get(`/admin/demos/${s}/tour/render`).then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { if (open) loadStatus(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy("save");
    try {
      await apiClient.patch(`/admin/demo-directory/${demo.id}`, { accent_color: accent, phase_titles: titles.map((t) => t.trim()) });
      toast.success("Branding saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(null); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy("logo");
    const fd = new FormData();
    fd.append("file", file);
    try { await apiClient.post(`/admin/demos/${s}/intro/logo`, fd); toast.success("Logo uploaded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Logo upload failed"); }
    finally { setBusy(null); }
  };

  const render = async () => {
    setBusy("render");
    try {
      const r = await rerecordAllBeats(s, (p) => setStatus((st) => ({ ...st, ...p })));
      toast.success(`Voice tour recorded · ${r.done} beats`);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Recording failed"); }
    finally { setBusy(null); loadStatus(); }
  };

  return (
    <div className="mt-3 border-t border-white/8 pt-3" data-testid={`demo-template-settings-${s}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`demo-template-toggle-${s}`}
        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-300 hover:text-emerald-200 transition-colors">
        <span className="w-3 h-3 rounded-full border border-white/20" style={{ background: accent }} />
        Template engine · branding & voice
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="space-y-2">
            {demo.template !== "exchange" && DEFAULT_TITLES.map((d, i) => (
              <input key={d} value={titles[i]} placeholder={`Phase ${i + 1} · ${d}`} data-testid={`demo-template-phase-${s}-${i + 1}`}
                onChange={(e) => setTitles((t) => t.map((x, j) => (j === i ? e.target.value : x)))} className={`${inputCls} w-full`} />
            ))}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <label className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.16em] uppercase text-slate-400">
                Accent
                <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid={`demo-template-accent-${s}`}
                  className="h-8 w-12 rounded-md bg-navy-950 border border-white/10 cursor-pointer p-0.5" />
              </label>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 hover:border-brand-cyan/50 text-slate-300 font-mono text-[10px] tracking-[0.14em] uppercase cursor-pointer transition-colors">
                {busy === "logo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload logo
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" data-testid={`demo-template-logo-${s}`}
                  onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
              <button type="button" onClick={save} disabled={!!busy} data-testid={`demo-template-save-${s}`}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-[10px] font-semibold tracking-[0.14em] uppercase transition disabled:opacity-50">
                {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save branding
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-400">Voice tour</div>
            <div className="mt-1 text-2xl font-mono text-white" data-testid={`demo-template-render-count-${s}`}>
              {busy === "render" && status ? `${status.done}/${status.total}` : status ? `${status.rendered}/${status.total}` : "—"}
              <span className="text-xs text-slate-500 ml-1">beats recorded</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Unrecorded beats use the browser voice. Pick the ElevenLabs voice on the demo page.</p>
            <button type="button" onClick={render} disabled={!!busy} data-testid={`demo-template-render-${s}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-brand-cyan text-navy-950 font-mono text-[10px] font-semibold tracking-[0.14em] uppercase hover:brightness-110 transition-[filter] disabled:opacity-50">
              {busy === "render" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
              {busy === "render" ? "Recording…" : "Render voice tour"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
